import React, { useEffect, useState } from 'react';

import {
  Skeleton, Stack,
} from '@mui/material';

import NotesCard from '../../../components/NotesCard';
import { AllFinishedMessage } from '../../../components/PageLayout';
import { useGetStaffsQuery } from '../../../services/staff';
import { useGetStudentQuery, useUpdateStudentNoteMutation } from '../../../services/students';
import { TypeOption } from '../../../services/types';
import { UserNote } from '../../../services/users/types';
import { UserGroup } from '../../../types/common';
import { joinNames } from '../../../utils/common/formatText';
import useError from '../../../utils/react/hooks/useError';

interface StudentsNotesCardProps {
  note: UserNote;
  assigneeOptions: {
    value: string;
    label: string;
  }[];
}

const StudentsNotesCard = ({
  note,
  assigneeOptions,
}: StudentsNotesCardProps) => {
  const { data: student, isLoading: isStudentLoading } =
    useGetStudentQuery({ studentId: note.userId });

  const [updateNote, {
    isError: isUpdateNoteError,
    error: updateNoteError,
  }] = useUpdateStudentNoteMutation();

  useError(isUpdateNoteError, updateNoteError);

  if (isStudentLoading) {
    return (
      <Skeleton height={100} variant="rectangular" />
    );
  }

  return (
    <NotesCard
      note={note}
      assigneeOptions={assigneeOptions}
      onNoteArchive={(creationDatetime) => updateNote({
        studentId: note.userId,
        creationDatetime,
        newAttributes: {
          active: false,
        },
      })}
      onNoteDueDateChange={(creationDatetime, dueDatetime) => updateNote({
        studentId: note.userId,
        creationDatetime,
        newAttributes: {
          dueDatetime,
        },
      })}
      onNoteAssigneeChange={(creationDatetime, assignee) => updateNote({
        studentId: note.userId,
        creationDatetime,
        newAttributes: {
          assignee,
        },
      })}
      userName={`${student?.data.name}`}
      userDetails={joinNames([student?.data.gradYear, student?.data.school])}
      userDetailsHref={`/admin/student/${note.userId}`}
    />
  );
};

interface StudentsTabProps {
  userNotes?: UserNote[];
}

const StudentsTab = ({ userNotes }: StudentsTabProps) => {
  const { data: managers,
    isLoading: isManagerLoading } = useGetStaffsQuery({
    group: UserGroup.Managers,
    excludeDisabled: true,
  });

  const [managerOpts, setManagerOpts] = useState<TypeOption[]>(
    (managers?.data || []).map((manager) => ({
      value: manager.id,
      label: joinNames([manager.firstName, manager.lastName]),
    })) || []);

  useEffect(() => {
    setManagerOpts((managers?.data || []).map((manager) => ({
      value: manager.id,
      label: joinNames([manager.firstName, manager.lastName]),
    })) || []);
  }, [managers]);

  if (isManagerLoading) {
    return (
      <Skeleton variant="rectangular" height={256} />
    );
  }

  return (
    <Stack direction="column" spacing={1}>
      {
        userNotes && userNotes.length > 0 ? (
          userNotes.map((userNote) => (
            <StudentsNotesCard
              key={userNote.id}
              note={userNote}
              assigneeOptions={managerOpts}
            />
          ))
        ) : (
          <AllFinishedMessage />
        )
      }
    </Stack>
  );
};

export default StudentsTab;
